import { AddItemInput } from '@/components/AddItemInput';
import { formatGroceryQuantity, stepGroceryQuantity } from '@/utils/groceryQuantity';
import GroceryDraggableList from '@/components/grocery/GroceryDraggableList';
import { GroceryListEmptyState } from '@/components/grocery/GroceryListEmptyState';
import { GroceryNoListsEmptyState } from '@/components/grocery/GroceryNoListsEmptyState';
import type { DraggableGroceryItem } from '@/components/grocery/GroceryDraggableList.types';
import { StaplesChips } from '@/components/StaplesChips';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { getContentWidth, SIDEBAR_BREAKPOINT, SIDEBAR_WIDTH } from '@/constants/layout';
import { MAX_GROCERY_LISTS_PER_HOUSEHOLD } from '@/constants/limits';
import { Brand, Neutrals, primaryAlpha } from '@/constants/theme';
import { useApp } from '@/context/AppContext';
import { useGroceryList } from '@/hooks/useGroceryList';
import { useGroceryListMutations } from '@/hooks/useGroceryListMutations';
import {
  useAddItems,
  useCreateList,
  useDeleteItem,
  useGroceryLists,
  useUpdateItem,
  useUpdateList,
  type AddGroceryItemInput,
  type GroceryItem,
} from '@/hooks/useGroceryLists';
import { useListStaples } from '@/hooks/useListStaples';
import { trackEvent } from '@/lib/analytics';
import {
  canDeleteList,
  getActiveListIdAfterDelete,
  getDisplayItems,
  getNextActiveListId,
  validateNewListName,
} from '@/lib/grocery-lists-page.logic';
import type { GroceryListItem, GroceryList as RealtimeGroceryList } from '@/types';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  KeyboardAvoidingView,
  LayoutAnimation,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

/** Convert realtime list (useGroceryList) to cache shape for getDisplayItems */
function realtimeListToCacheList(
  list: RealtimeGroceryList | null
): { id: string; name: string; items: GroceryItem[]; createdAt: number } | null {
  if (!list) return null;
  return {
    id: list.id,
    name: list.name,
    createdAt: new Date(list.created_at).getTime(),
    items: list.items.map((i: GroceryListItem) => ({
      id: i.id,
      name: i.name,
      category: i.category,
      checked: i.checked,
      normalizedName: i.normalizedName,
      quantity: i.quantity,
      unit: i.unit,
      sourceRecipes: i.sourceRecipes,
      recipeId: i.sourceRecipes?.[0],
      addedAt: new Date(i.addedAt).getTime(),
      position: i.position,
    })),
  };
}

/** Convert cache GroceryItem to DraggableGroceryItem for the draggable list */
function toDraggableItem(item: GroceryItem): DraggableGroceryItem {
  return {
    id: item.id,
    name: item.name,
    quantity: item.quantity != null ? String(item.quantity) : '',
    unit: item.unit ?? '',
    checked: item.checked,
    sort_order: item.position,
  };
}

const NEW_LIST_PLACEHOLDERS = [
  'e.g., Weekend Shop',
  'e.g., Costco Run',
  'e.g., Farmers Market',
  'e.g., Weekly Essentials',
  'e.g., Meal Prep',
  'e.g., Holiday Dinner',
  'e.g., Quick Essentials',
];

/** Tailwind-like neutrals for row design */
const RowNeutrals = {
  border: '#f5f5f5', // neutral-100
  handle: '#a3a3a3', // neutral-400
  text: '#111827', // neutral-900
  muted: '#9ca3af', // neutral-400 for checked qty
  faintPlus: '#d4d4d4', // neutral-300
} as const;

interface GroceryListItemRowProps {
  item: GroceryItem;
  listId: string;
  onToggleChecked: (listId: string, itemId: string, currentlyChecked: boolean) => void;
  strikethroughWhenChecked: boolean;
  /** Optional drag handle node (left side); undefined for checked/static rows */
  dragHandle?: React.ReactNode;
  /** Called immediately for optimistic update; parent debounces persist */
  onQuantityChange?: (itemId: string, quantity: number) => void;
  /** Long-press minus at 1: request remove item (parent shows confirmation); receives (itemId, displayName) */
  onRequestRemoveItem?: (itemId: string, displayName: string) => void;
  /** Called when user commits an inline name edit; receives (itemId, newName) */
  onRenameItem?: (itemId: string, name: string) => void;
  /** When true, no bottom border (last item in section) */
  isLastInSection?: boolean;
  /** When true, row uses card background (mobile list-as-scroll layout) */
  listHasCardBackground?: boolean;
}

const GroceryListItemRow = memo(
  function GroceryListItemRow({
    item,
    listId,
    onToggleChecked,
    strikethroughWhenChecked,
    dragHandle,
    onQuantityChange,
    onRequestRemoveItem,
    onRenameItem,
    isLastInSection = false,
    listHasCardBackground = false,
  }: GroceryListItemRowProps) {
    const isChecked = item.checked;
    const [isEditingName, setIsEditingName] = useState(false);
    const [editNameValue, setEditNameValue] = useState('');
    const hasQuantity = item.quantity != null && typeof item.quantity === 'number';
    const qty = hasQuantity ? item.quantity ?? 0 : null;
    const unit = item.unit ?? '';
    const quantityLabel =
      qty != null ? `${formatGroceryQuantity(qty)}${unit ? ` ${unit}` : ''}`.trim() : '';

    const [quantityExpanded, setQuantityExpanded] = useState(false);
    const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearCollapseTimer = useCallback(() => {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
    }, []);

    const scheduleCollapse = useCallback(() => {
      clearCollapseTimer();
      collapseTimerRef.current = setTimeout(() => {
        if (Platform.OS !== 'web') {
          LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
        }
        setQuantityExpanded(false);
        collapseTimerRef.current = null;
      }, 3000);
    }, [clearCollapseTimer]);

    useEffect(() => () => clearCollapseTimer(), [clearCollapseTimer]);

    const handleQuantityAreaPress = useCallback(() => {
      if (isChecked) return;
      if (Platform.OS !== 'web') {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      }
      setQuantityExpanded(true);
      scheduleCollapse();
    }, [isChecked, scheduleCollapse]);

    const handlePlus = useCallback(() => {
      if (isChecked || !onQuantityChange) return;
      const current = item.quantity != null && typeof item.quantity === 'number' ? item.quantity : 0;
      onQuantityChange(item.id, stepGroceryQuantity(current, 1));
      scheduleCollapse();
    }, [item.id, item.quantity, isChecked, onQuantityChange, scheduleCollapse]);

    const handleMinus = useCallback(() => {
      if (isChecked || !onQuantityChange) return;
      const current = item.quantity != null && typeof item.quantity === 'number' ? item.quantity : 1;
      const next = stepGroceryQuantity(current, -1);
      if (next === current) return;
      onQuantityChange(item.id, next);
      scheduleCollapse();
    }, [item.id, item.quantity, isChecked, onQuantityChange, scheduleCollapse]);

    const handleMinusLongPress = useCallback(() => {
      if (isChecked || !onRequestRemoveItem) return;
      const current = item.quantity != null && typeof item.quantity === 'number' ? item.quantity : 0;
      if (current <= 1) onRequestRemoveItem(item.id, item.name.trim() || 'Item');
    }, [item.id, item.name, item.quantity, isChecked, onRequestRemoveItem]);

    const commitNameEdit = useCallback(() => {
      const trimmed = editNameValue.trim() || 'Item';
      if (trimmed === (item.name.trim() || 'Item')) {
        setIsEditingName(false);
        return;
      }
      onRenameItem?.(item.id, trimmed);
      setIsEditingName(false);
    }, [editNameValue, item.id, item.name, onRenameItem]);

    const startEditingName = useCallback(() => {
      if (isChecked) return;
      setEditNameValue(item.name.trim() || '');
      setIsEditingName(true);
    }, [isChecked, item.name]);

    const showExpandedControls = quantityExpanded && !isChecked && hasQuantity;
    const quantityColor = isChecked ? RowNeutrals.muted : Brand.primary;

    return (
      <View
        style={[
          styles.itemRow,
          isChecked && styles.itemRowChecked,
          isLastInSection && styles.itemRowLastInSection,
          listHasCardBackground && styles.itemRowCardBg,
        ]}
      >
        {dragHandle != null ? (
          <View style={styles.dragHandleSlot}>
            <View style={styles.dragHandleOverflow}>{dragHandle}</View>
          </View>
        ) : null}
        <View style={styles.checkboxTouchTarget}>
          <Checkbox
            checked={item.checked}
            onCheckedChange={() => onToggleChecked(listId, item.id, item.checked)}
          />
        </View>
        <View style={styles.itemNameBlock}>
          {isEditingName ? (
            <TextInput
              style={[
                styles.itemText,
                styles.itemNameInput,
                item.checked && strikethroughWhenChecked && styles.itemTextStrikethrough,
              ]}
              value={editNameValue}
              onChangeText={setEditNameValue}
              onBlur={commitNameEdit}
              onSubmitEditing={commitNameEdit}
              autoFocus
              selectTextOnFocus
              accessibilityLabel="Item name"
              returnKeyType="done"
            />
          ) : (
            <Pressable
              onPress={startEditingName}
              style={styles.itemNamePressable}
              accessibilityLabel="Edit item name"
              accessibilityRole="button"
            >
              <Text
                style={[
                  styles.itemText,
                  item.checked && strikethroughWhenChecked && styles.itemTextStrikethrough,
                ]}
              >
                {item.name.trim() || 'Item'}
              </Text>
            </Pressable>
          )}
        </View>
        <View style={styles.quantityArea}>
          {!hasQuantity ? (
            <Pressable
              onPress={handlePlus}
              disabled={isChecked}
              style={({ pressed }) => [
                styles.quantityValueTouchTarget,
                pressed && !isChecked && styles.quantityBtnPressed,
              ]}
              accessibilityLabel="Set quantity to 1"
            >
              <Ionicons name="add" size={20} color={RowNeutrals.faintPlus} />
            </Pressable>
          ) : showExpandedControls ? (
            <View style={styles.quantityExpandedRow}>
              <Pressable
                onPress={handleMinus}
                onLongPress={handleMinusLongPress}
                style={({ pressed }) => [
                  styles.quantityBtn,
                  pressed && styles.quantityBtnPressed,
                ]}
                accessibilityLabel={qty === 1 ? 'Decrement or long-press to remove item' : 'Decrement quantity'}
              >
                <Ionicons name="remove" size={20} color={Brand.primary} />
              </Pressable>
              <Text style={[styles.quantityLabel, { color: quantityColor }]} numberOfLines={1}>
                {quantityLabel}
              </Text>
              <Pressable
                onPress={handlePlus}
                style={({ pressed }) => [
                  styles.quantityBtn,
                  pressed && styles.quantityBtnPressed,
                ]}
                accessibilityLabel="Increment quantity"
              >
                <Ionicons name="add" size={20} color={Brand.primary} />
              </Pressable>
            </View>
          ) : (
            <Pressable
              onPress={handleQuantityAreaPress}
              disabled={isChecked}
              style={({ pressed }) => [
                styles.quantityValueTouchTarget,
                pressed && !isChecked && styles.quantityBtnPressed,
              ]}
              accessibilityLabel="Change quantity"
            >
              <Text style={[styles.quantityValueOnly, { color: quantityColor }]} numberOfLines={1}>
                {quantityLabel}
              </Text>
            </Pressable>
          )}
        </View>
        <Pressable
          onPress={() => onRequestRemoveItem?.(item.id, item.name.trim() || 'Item')}
          style={({ pressed }) => [styles.rowClearButton, pressed && styles.rowClearButtonPressed]}
          accessibilityLabel="Remove item"
        >
          <Ionicons name="trash-outline" size={20} color={Neutrals.textMuted} />
        </Pressable>
      </View>
    );
  },
  (prev, next) =>
    prev.item.id === next.item.id &&
    prev.item.checked === next.item.checked &&
    prev.item.name === next.item.name &&
    prev.item.quantity === next.item.quantity &&
    prev.item.unit === next.item.unit &&
    prev.listId === next.listId &&
    prev.strikethroughWhenChecked === next.strikethroughWhenChecked &&
    prev.dragHandle === next.dragHandle &&
    prev.onQuantityChange === next.onQuantityChange &&
    prev.onRequestRemoveItem === next.onRequestRemoveItem &&
    prev.onRenameItem === next.onRenameItem &&
    prev.isLastInSection === next.isLastInSection &&
    prev.listHasCardBackground === next.listHasCardBackground
);

export default function GroceryLists() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { settings } = useApp();
  const { lists: groceryLists, loading: listsLoading, error: listsError, refetch, deleteList } = useGroceryLists();
  const [activeListId, setActiveListId] = useState('');
  const { listStaples, loading: staplesLoading, refetch: refetchStaples } = useListStaples(
    activeListId || null
  );
  const { createList, loading: createListLoading, error: createListError } = useCreateList();
  const { addItems } = useAddItems();
  const { updateItem } = useUpdateItem();
  const { updateList } = useUpdateList();
  const { deleteItem: deleteItemFromList } = useDeleteItem();

  const { list: realtimeList, setList: setRealtimeList } = useGroceryList(
    activeListId || undefined
  );
  const realtimeMutations = useGroceryListMutations({
    listId: activeListId,
    list: realtimeList,
    setList: setRealtimeList,
  });

  // Re-sync from cache when screen gains focus (cache hit = no network request)
  useFocusEffect(
    useCallback(() => {
      refetch();
      refetchStaples();
    }, [refetch, refetchStaples])
  );

  const [windowWidth, setWindowWidth] = useState(Dimensions.get('window').width);

  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => setWindowWidth(window.width));
    return () => sub?.remove();
  }, []);

  // Keep activeListId in sync when lists load or change
  useEffect(() => {
    const next = getNextActiveListId(groceryLists, activeListId);
    if (next !== activeListId) setActiveListId(next);
  }, [groceryLists, activeListId]);

  const contentWidth = getContentWidth(windowWidth);
  const dragOverlayBounds = useMemo(() => {
    if (Platform.OS !== 'web' || windowWidth < SIDEBAR_BREAKPOINT) return undefined;
    const { width, height } = Dimensions.get('window');
    return { left: SIDEBAR_WIDTH, top: 0, width: width - SIDEBAR_WIDTH, height };
  }, [windowWidth]);
  const [showNewListDialog, setShowNewListDialog] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListPlaceholder, setNewListPlaceholder] = useState(NEW_LIST_PLACEHOLDERS[0]);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [listToDelete, setListToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleteListLoading, setDeleteListLoading] = useState(false);
  const [resetListLoading, setResetListLoading] = useState(false);
  const [showRemoveItemModal, setShowRemoveItemModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState<{ id: string; name: string } | null>(null);
  const [removeItemLoading, setRemoveItemLoading] = useState(false);
  const [pendingQuantities, setPendingQuantities] = useState<Record<string, number>>({});
  const quantityDebounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalTitle, setAlertModalTitle] = useState('');
  const [alertModalMessage, setAlertModalMessage] = useState('');

  const hasReachedLimit = groceryLists.length >= MAX_GROCERY_LISTS_PER_HOUSEHOLD;

  const showAlert = useCallback((title: string, message: string) => {
    setAlertModalTitle(title);
    setAlertModalMessage(message);
    setShowAlertModal(true);
  }, []);

  // Pick a random placeholder when the Create New List modal opens
  useEffect(() => {
    if (showNewListDialog) {
      setNewListPlaceholder(
        NEW_LIST_PLACEHOLDERS[Math.floor(Math.random() * NEW_LIST_PLACEHOLDERS.length)]
      );
    }
  }, [showNewListDialog]);

  const handleCreateList = async () => {
    if (hasReachedLimit) {
      showAlert('Limit reached', `Maximum of ${MAX_GROCERY_LISTS_PER_HOUSEHOLD} lists. Delete a list to create a new one.`);
      return;
    }
    const validation = validateNewListName(newListName);
    if (!validation.valid) {
      showAlert('Error', validation.error);
      return;
    }
    const nameToUse = validation.trimmed;
    try {
      const result = await createList(nameToUse);
      if (result) {
        trackEvent('Grocery list created', { list_id: result.list.id });
        setActiveListId(result.list.id);
        setNewListName('');
        setShowNewListDialog(false);
        refetch(true, result.newHouseholdId).catch(() => {
          // List was created; refetch failed. User can pull to refresh.
        });
      } else {
        // Error is shown in-line in the create list modal (e.g. no household)
      }
    } catch {
      showAlert('Error', 'Failed to create list. Please try again.');
    }
  };

  const handleConfirmDeleteList = async () => {
    if (!listToDelete || !canDeleteList(groceryLists)) return;
    setDeleteListLoading(true);
    try {
      await deleteList(listToDelete.id);
      const nextId = getActiveListIdAfterDelete(listToDelete.id, activeListId, groceryLists);
      if (nextId !== activeListId) setActiveListId(nextId);
      setShowDeleteConfirmModal(false);
      setListToDelete(null);
    } catch {
      showAlert('Error', 'Failed to delete list. Please try again.');
    } finally {
      setDeleteListLoading(false);
    }
  };

  const openDeleteConfirmModal = useCallback(
    (list: { id: string; name: string }) => {
      if (!canDeleteList(groceryLists)) {
        showAlert('Error', 'Cannot delete the last list');
        return;
      }
      setListToDelete(list);
      setShowDeleteConfirmModal(true);
    },
    [groceryLists, showAlert]
  );

  const handleConfirmResetList = useCallback(async () => {
    if (!activeListId) return;
    setResetListLoading(true);
    try {
      const useRealtime = realtimeList?.id === activeListId;
      if (useRealtime) {
        await realtimeMutations.clearAllItems();
      } else {
        await updateList(activeListId, { items: [] });
      }
      refetch(true).catch(() => {});
      setShowResetConfirmModal(false);
    } catch {
      showAlert('Error', 'Failed to reset list. Please try again.');
    } finally {
      setResetListLoading(false);
    }
  }, [activeListId, realtimeList?.id, realtimeMutations, updateList, refetch, showAlert]);

  const handleAddItem = useCallback(
    async (itemName: string) => {
      const trimmed = itemName.trim();
      if (!trimmed || !activeListId) return;
      setIsAddingItem(true);
      try {
        const useRealtime = realtimeList?.id === activeListId;
        if (useRealtime) {
          await realtimeMutations.addItems([{ name: trimmed, category: 'Other', checked: false }]);
          refetch(true).catch(() => {});
        } else {
          const newItem: AddGroceryItemInput = { name: trimmed, category: 'Other', checked: false };
          await addItems(activeListId, [newItem]);
          refetch().catch(() => {});
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to add item. Please try again.';
        showAlert('Error', message);
      } finally {
        setIsAddingItem(false);
      }
    },
    [activeListId, realtimeList?.id, realtimeMutations, addItems, refetch, showAlert]
  );

  const handleStaplePress = useCallback(
    async (staple: { item_name: string; category: string }) => {
      if (!activeListId) return;
      setIsAddingItem(true);
      try {
        const useRealtime = realtimeList?.id === activeListId;
        if (useRealtime) {
          await realtimeMutations.addItems([
            { name: staple.item_name, category: staple.category || 'Other', checked: false },
          ]);
          refetch(true).catch(() => {});
        } else {
          const newItem: AddGroceryItemInput = {
            name: staple.item_name,
            category: staple.category || 'Other',
            checked: false,
          };
          await addItems(activeListId, [newItem]);
          refetch().catch(() => {});
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to add item. Please try again.';
        showAlert('Error', message);
      } finally {
        setIsAddingItem(false);
      }
    },
    [activeListId, realtimeList?.id, realtimeMutations, addItems, refetch, showAlert]
  );

  const handleManageStaples = useCallback(() => {
    if (!activeListId) return;
    router.push({
      pathname: '/(tabs)/grocery-lists/manage-staples',
      params: { listId: activeListId },
    });
  }, [router, activeListId]);

  const handleToggleItemChecked = useCallback(
    (listId: string, itemId: string, currentlyChecked: boolean) => {
      const newChecked = !currentlyChecked;
      const useRealtime = realtimeList?.id === activeListId && listId === activeListId;
      if (useRealtime) {
        realtimeMutations
          .updateItem(itemId, { checked: newChecked })
          .then(() => {
            trackEvent('Grocery item toggled', { list_id: listId, item_id: itemId, checked: newChecked });
            refetch(true).catch(() => {});
          })
          .catch(() => {
            showAlert('Error', 'Failed to update item. Please try again.');
          });
      } else {
        updateItem(listId, itemId, { checked: newChecked })
          .then(() => {
            trackEvent('Grocery item toggled', { list_id: listId, item_id: itemId, checked: newChecked });
          })
          .catch(() => {
            showAlert('Error', 'Failed to update item. Please try again.');
          });
      }
    },
    [activeListId, realtimeList?.id, realtimeMutations, updateItem, refetch, showAlert]
  );

  const schedulePersistQuantity = useCallback(
    (itemId: string, quantity: number) => {
      if (!activeListId) return;
      const key = itemId;
      if (quantityDebounceRef.current[key]) {
        clearTimeout(quantityDebounceRef.current[key]);
      }
      quantityDebounceRef.current[key] = setTimeout(() => {
        delete quantityDebounceRef.current[key];
        const useRealtime = realtimeList?.id === activeListId;
        if (useRealtime) {
          realtimeMutations.updateItem(itemId, { quantity }).then(() => refetch(true).catch(() => {}));
        } else {
          updateItem(activeListId, itemId, { quantity }).then(() => {
            refetch().catch(() => {});
            setPendingQuantities((prev) => {
              const next = { ...prev };
              delete next[itemId];
              return next;
            });
          });
        }
      }, 600);
    },
    [activeListId, realtimeList?.id, realtimeMutations, updateItem, refetch]
  );

  const handleQuantityChange = useCallback(
    (itemId: string, quantity: number) => {
      const useRealtime = realtimeList?.id === activeListId;
      if (useRealtime && realtimeList) {
        setRealtimeList((prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((i) => (i.id === itemId ? { ...i, quantity } : i)),
              }
            : null
        );
      } else {
        setPendingQuantities((prev) => ({ ...prev, [itemId]: quantity }));
      }
      schedulePersistQuantity(itemId, quantity);
    },
    [activeListId, realtimeList, setRealtimeList, schedulePersistQuantity]
  );

  const handleRequestRemoveItem = useCallback((itemId: string, displayName: string) => {
    setItemToRemove({ id: itemId, name: displayName });
    setShowRemoveItemModal(true);
  }, []);

  const handleRenameItem = useCallback(
    (listId: string, itemId: string, name: string) => {
      const useRealtime = realtimeList?.id === activeListId && listId === activeListId;
      if (useRealtime) {
        realtimeMutations
          .updateItem(itemId, { name })
          .then(() => refetch(true).catch(() => {}))
          .catch(() => {
            showAlert('Error', 'Failed to update item. Please try again.');
          });
      } else {
        updateItem(listId, itemId, { name })
          .then(() => refetch().catch(() => {}))
          .catch(() => {
            showAlert('Error', 'Failed to update item. Please try again.');
          });
      }
    },
    [activeListId, realtimeList?.id, realtimeMutations, updateItem, refetch, showAlert]
  );

  const handleConfirmRemoveItem = useCallback(async () => {
    if (!itemToRemove || !activeListId) return;
    setRemoveItemLoading(true);
    try {
      const useRealtime = realtimeList?.id === activeListId;
      if (useRealtime) {
        await realtimeMutations.deleteItem(itemToRemove.id);
        refetch(true).catch(() => {});
      } else {
        await deleteItemFromList(activeListId, itemToRemove.id);
        refetch().catch(() => {});
      }
      setShowRemoveItemModal(false);
      setItemToRemove(null);
    } catch {
      showAlert('Error', 'Failed to remove item. Please try again.');
    } finally {
      setRemoveItemLoading(false);
    }
  }, [itemToRemove, activeListId, realtimeList?.id, realtimeMutations, deleteItemFromList, refetch, showAlert]);

  const activeList =
    realtimeListToCacheList(activeListId && realtimeList?.id === activeListId ? realtimeList : null) ??
    groceryLists.find((l) => l.id === activeListId) ??
    null;
  const displayItems = useMemo(
    () => getDisplayItems(activeList ?? null, settings.checkedItemsBehavior),
    [activeList, settings.checkedItemsBehavior]
  );

  const uncheckedItems = useMemo(
    () => displayItems.filter((i) => !i.checked).map(toDraggableItem),
    [displayItems]
  );
  const checkedItems = useMemo(
    () => displayItems.filter((i) => i.checked).map(toDraggableItem),
    [displayItems]
  );

  const displayItemsById = useMemo(() => new Map(displayItems.map((i) => [i.id, i])), [displayItems]);
  const strikethroughWhenChecked = settings.checkedItemsBehavior === 'strikethrough';

  const handleReorder = useCallback(
    (reorderedItems: DraggableGroceryItem[]) => {
      if (!realtimeList || realtimeList.id !== activeListId) return;
      const uncheckedIds = reorderedItems.map((i) => i.id);
      const itemById = new Map(realtimeList.items.map((i: GroceryListItem) => [i.id, i]));
      const reorderedUnchecked = uncheckedIds
        .map((id) => itemById.get(id))
        .filter(Boolean) as GroceryListItem[];
      const checked = realtimeList.items.filter((i: GroceryListItem) => i.checked);
      const newItems = [...reorderedUnchecked, ...checked];
      realtimeMutations.reorderItems(newItems);
    },
    [realtimeList, activeListId, realtimeMutations]
  );

  const isNative = Platform.OS === 'ios' || Platform.OS === 'android';
  const useListAsScroll = isNative && activeList != null && activeList.items.length > 0;

  const renderDraggableItem = useCallback(
    (item: DraggableGroceryItem, dragHandle?: React.ReactNode) => {
      const fullItem = displayItemsById.get(item.id);
      if (!fullItem) return null;
      const displayItem: GroceryItem = {
        ...fullItem,
        quantity: pendingQuantities[fullItem.id] ?? fullItem.quantity,
      };
      const isUnchecked = dragHandle != null;
      const sectionItems = isUnchecked ? uncheckedItems : checkedItems;
      const isLastInSection = sectionItems.length > 0 && sectionItems[sectionItems.length - 1].id === item.id;
      return (
        <GroceryListItemRow
          item={displayItem}
          listId={activeListId}
          onToggleChecked={handleToggleItemChecked}
          strikethroughWhenChecked={strikethroughWhenChecked}
          dragHandle={dragHandle}
          onQuantityChange={handleQuantityChange}
          onRequestRemoveItem={handleRequestRemoveItem}
          onRenameItem={(itemId, name) => handleRenameItem(activeListId, itemId, name)}
          isLastInSection={isLastInSection}
          listHasCardBackground={useListAsScroll}
        />
      );
    },
    [
      displayItemsById,
      activeListId,
      handleToggleItemChecked,
      strikethroughWhenChecked,
      pendingQuantities,
      handleQuantityChange,
      handleRequestRemoveItem,
      handleRenameItem,
      uncheckedItems,
      checkedItems,
      useListAsScroll,
    ]
  );

  const listHeaderElement = useMemo(
    () =>
      activeList ? (
        <View style={styles.listContent}>
          <Text style={styles.pageTitle}>Grocery Lists</Text>
          <View style={styles.headerRow}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.tabsScroll}
              contentContainerStyle={styles.tabsContent}
            >
              {groceryLists.map((list, index) => (
                <TouchableOpacity
                  key={list.id}
                  testID={`grocery-list-${index}`}
                  onPress={() => setActiveListId(list.id)}
                  style={[styles.tabPill, activeListId === list.id && styles.tabPillActive]}
                >
                  <Text
                    style={[
                      styles.tabPillText,
                      activeListId === list.id && styles.tabPillTextActive,
                    ]}
                    numberOfLines={1}
                  >
                    {list.name}
                  </Text>
                  <View style={styles.tabPillBadge}>
                    <Text style={[styles.tabPillBadgeText, activeListId === list.id && styles.tabPillBadgeTextActive]}>
                      {list.items.length}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>
            {/* List header is only used on native (useListAsScroll); icon buttons on same row */}
            <TouchableOpacity
              onPress={() => {
                if (hasReachedLimit) {
                  showAlert('Limit reached', `Maximum of ${MAX_GROCERY_LISTS_PER_HOUSEHOLD} lists. Delete a list to create a new one.`);
                  return;
                }
                setShowNewListDialog(true);
              }}
              disabled={hasReachedLimit}
              style={[styles.headerIconButton, styles.newListIconButton, hasReachedLimit && styles.headerIconButtonDisabled]}
              accessibilityLabel="New list"
            >
              <Ionicons name="add" size={24} color={hasReachedLimit ? Neutrals.textMuted : '#fff'} />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => setShowResetConfirmModal(true)}
              disabled={activeList.items.length === 0}
              style={[styles.headerIconButton, activeList.items.length === 0 && styles.headerIconButtonDisabled]}
              accessibilityLabel="Reset list"
            >
              <Ionicons name="refresh-outline" size={22} color={activeList.items.length === 0 ? Neutrals.textMuted : Neutrals.textSecondary} />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => openDeleteConfirmModal({ id: activeList.id, name: activeList.name })}
              disabled={groceryLists.length === 1}
              style={[styles.headerIconButton, styles.deleteIconButton, groceryLists.length === 1 && styles.headerIconButtonDisabled]}
              accessibilityLabel="Delete list"
            >
              <Ionicons name="trash-outline" size={22} color={groceryLists.length === 1 ? Neutrals.destructiveSoft : Brand.destructive} />
            </TouchableOpacity>
          </View>
          <StaplesChips
            staples={listStaples}
            loading={staplesLoading}
            onStaplePress={handleStaplePress}
            onManagePress={handleManageStaples}
            disabled={isAddingItem || !activeListId}
          />
          <Card style={styles.progressCard}>
            <CardContent style={styles.progressContent}>
              <View style={styles.progressHeader}>
                <Text style={styles.progressLabel}>Shopping Progress</Text>
                <Text style={styles.progressValue}>
                  {activeList.items.filter((i) => i.checked).length} / {activeList.items.length}
                </Text>
              </View>
              <View style={styles.progressBar}>
                <View
                  style={[
                    styles.progressFill,
                    {
                      width: `${(activeList.items.filter((i) => i.checked).length / activeList.items.length) * 100}%`,
                    },
                  ]}
                />
              </View>
            </CardContent>
          </Card>
          {/* Card “start” so list items sit on card background; header above uses app (gradient) background */}
          <View style={styles.listCardTop} />
        </View>
      ) : null,
    [
      activeList,
      activeListId,
      groceryLists,
      hasReachedLimit,
      isAddingItem,
      listStaples,
      staplesLoading,
      showAlert,
      handleStaplePress,
      handleManageStaples,
      openDeleteConfirmModal,
    ]
  );

  const listFooterElement = useMemo(
    () => (
      <View style={styles.listCardBottom}>
        <AddItemInput
          onAddItem={handleAddItem}
          placeholder="Add item..."
          disabled={isAddingItem}
        />
      </View>
    ),
    [handleAddItem, isAddingItem]
  );

  if (listsLoading && groceryLists.length === 0) {
    return (
      <View style={[styles.container, styles.centered]}>
        <LinearGradient colors={[...Brand.primaryGradient]} style={styles.background} />
        <ActivityIndicator size="large" color={Brand.primary} />
      </View>
    );
  }

  if (listsError && groceryLists.length === 0) {
    return (
      <View style={[styles.container, styles.centered]}>
        <LinearGradient colors={[...Brand.primaryGradient]} style={styles.background} />
        <Text style={styles.errorText}>Failed to load lists</Text>
        <Button onPress={() => refetch()}>Retry</Button>
      </View>
    );
  }

  if (groceryLists.length === 0) {
    return (
      <>
        <View style={styles.container}>
          <LinearGradient
            colors={[...Brand.primaryGradient]}
            style={styles.background}
          />
          <GroceryNoListsEmptyState onCreateList={() => setShowNewListDialog(true)} />
        </View>
        <Modal
          visible={showNewListDialog}
          transparent
          animationType="fade"
          onRequestClose={() => setShowNewListDialog(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <Text style={styles.modalTitle}>Create New List</Text>
              <Input
                value={newListName}
                onChangeText={setNewListName}
                placeholder={newListPlaceholder}
                onSubmitEditing={handleCreateList}
              />
              {createListError ? (
                <Text style={styles.modalErrorText}>{createListError.message}</Text>
              ) : null}
              <View style={styles.modalActions}>
                <Button variant="outline" onPress={() => setShowNewListDialog(false)} disabled={createListLoading} style={styles.modalButton}>
                  Cancel
                </Button>
                <Button onPress={handleCreateList} loading={createListLoading} disabled={createListLoading} style={styles.modalButton}>
                  Create
                </Button>
              </View>
            </View>
          </View>
        </Modal>
      </>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={0}
    >
      <View style={styles.container} testID="lists-screen">
        <LinearGradient
          colors={[...Brand.primaryGradient]}
          style={styles.background}
        />
        {useListAsScroll ? (
          <View style={[styles.scrollView, { paddingTop: (isNative ? 10 : 16) + insets.top }]}>
            <GroceryDraggableList
              uncheckedItems={uncheckedItems}
              checkedItems={checkedItems}
              onReorder={handleReorder}
              renderItem={renderDraggableItem}
              ListHeaderComponent={listHeaderElement}
              ListFooterComponent={listFooterElement}
              contentContainerStyle={[
                styles.scrollContent,
                {
                  maxWidth: Math.min(contentWidth, 864),
                  paddingHorizontal: 16,
                  paddingTop: 0,
                  paddingBottom: 100,
                },
              ]}
              dragOverlayBounds={dragOverlayBounds}
            />
          </View>
        ) : (
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={[
              styles.scrollContent,
              { maxWidth: Math.min(contentWidth, 864), paddingTop: 16 + insets.top },
            ]}
            showsVerticalScrollIndicator={true}
            keyboardShouldPersistTaps="always"
          >
            <Text style={styles.pageTitle}>Grocery Lists</Text>

          {/* List pills row; on mobile, New List and Delete are icon buttons on same row */}
          <View style={styles.headerRow}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.tabsScroll}
              contentContainerStyle={styles.tabsContent}
            >
              {groceryLists.map((list, index) => (
                <TouchableOpacity
                  key={list.id}
                  testID={`grocery-list-${index}`}
                  onPress={() => setActiveListId(list.id)}
                  style={[styles.tabPill, activeListId === list.id && styles.tabPillActive]}
                >
                  <Text
                    style={[
                      styles.tabPillText,
                      activeListId === list.id && styles.tabPillTextActive,
                    ]}
                    numberOfLines={1}
                  >
                    {list.name}
                  </Text>
                  <View style={styles.tabPillBadge}>
                    <Text style={[styles.tabPillBadgeText, activeListId === list.id && styles.tabPillBadgeTextActive]}>
                      {list.items.length}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>
            {isNative && activeList && (
              <>
                <TouchableOpacity
                  onPress={() => {
                    if (hasReachedLimit) {
                      showAlert('Limit reached', `Maximum of ${MAX_GROCERY_LISTS_PER_HOUSEHOLD} lists. Delete a list to create a new one.`);
                      return;
                    }
                    setShowNewListDialog(true);
                  }}
                  disabled={hasReachedLimit}
                  style={[styles.headerIconButton, styles.newListIconButton, hasReachedLimit && styles.headerIconButtonDisabled]}
                  accessibilityLabel="New list"
                >
                  <Ionicons name="add" size={24} color={hasReachedLimit ? Neutrals.textMuted : '#fff'} />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => setShowResetConfirmModal(true)}
                  disabled={activeList.items.length === 0}
                  style={[styles.headerIconButton, activeList.items.length === 0 && styles.headerIconButtonDisabled]}
                  accessibilityLabel="Reset list"
                >
                  <Ionicons name="refresh-outline" size={22} color={activeList.items.length === 0 ? Neutrals.textMuted : Neutrals.textSecondary} />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => openDeleteConfirmModal({ id: activeList.id, name: activeList.name })}
                  disabled={groceryLists.length === 1}
                  style={[styles.headerIconButton, styles.deleteIconButton, groceryLists.length === 1 && styles.headerIconButtonDisabled]}
                  accessibilityLabel="Delete list"
                >
                  <Ionicons name="trash-outline" size={22} color={groceryLists.length === 1 ? Neutrals.destructiveSoft : Brand.destructive} />
                </TouchableOpacity>
              </>
            )}
          </View>

          {/* Web: separate row for New List / Delete text buttons */}
          {!isNative && activeList && (
            <View style={styles.actionsRow}>
              {hasReachedLimit && (
                <Text style={styles.limitMessage}>Maximum of {MAX_GROCERY_LISTS_PER_HOUSEHOLD} lists reached</Text>
              )}
              <TouchableOpacity
                onPress={() => {
                  if (hasReachedLimit) {
                    showAlert('Limit reached', `Maximum of ${MAX_GROCERY_LISTS_PER_HOUSEHOLD} lists. Delete a list to create a new one.`);
                    return;
                  }
                  setShowNewListDialog(true);
                }}
                disabled={hasReachedLimit}
                style={[styles.newListButton, hasReachedLimit && styles.newListButtonDisabled]}
              >
                <Ionicons name="add" size={16} color={hasReachedLimit ? Neutrals.textMuted : '#fff'} />
                <Text style={[styles.newListButtonText, hasReachedLimit && styles.newListButtonTextDisabled]}>
                  New List
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setShowResetConfirmModal(true)}
                disabled={activeList.items.length === 0}
                style={[styles.resetButton, activeList.items.length === 0 && styles.buttonDisabled]}
              >
                <Ionicons name="refresh-outline" size={16} color={activeList.items.length === 0 ? Neutrals.textMuted : Neutrals.textSecondary} />
                <Text style={[styles.resetButtonText, activeList.items.length === 0 && styles.resetButtonTextDisabled]}>
                  Reset
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => openDeleteConfirmModal({ id: activeList.id, name: activeList.name })}
                disabled={groceryLists.length === 1}
                style={[styles.deleteButton, groceryLists.length === 1 && styles.buttonDisabled]}
              >
                <Ionicons name="trash-outline" size={16} color={groceryLists.length === 1 ? Neutrals.destructiveSoft : Brand.destructive} />
                <Text style={[styles.deleteButtonText, groceryLists.length === 1 && styles.buttonDisabledText]}>
                  Delete
                </Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Active List Content - takes majority of page, grows with items */}
          {activeList && (
            <View style={styles.listContent}>
              <StaplesChips
                staples={listStaples}
                loading={staplesLoading}
                onStaplePress={handleStaplePress}
                onManagePress={handleManageStaples}
                disabled={isAddingItem || !activeListId}
              />

              {activeList.items.length === 0 ? (
                <GroceryListEmptyState>
                  <AddItemInput
                    onAddItem={handleAddItem}
                    placeholder="Add item..."
                    disabled={isAddingItem}
                  />
                </GroceryListEmptyState>
              ) : (
                <>
                  <Card style={styles.progressCard}>
                    <CardContent style={styles.progressContent}>
                      <View style={styles.progressHeader}>
                        <Text style={styles.progressLabel}>Shopping Progress</Text>
                        <Text style={styles.progressValue}>
                          {activeList.items.filter((i) => i.checked).length} / {activeList.items.length}
                        </Text>
                      </View>
                      <View style={styles.progressBar}>
                        <View
                          style={[
                            styles.progressFill,
                            {
                              width: `${(activeList.items.filter((i) => i.checked).length / activeList.items.length) * 100}%`,
                            },
                          ]}
                        />
                      </View>
                    </CardContent>
                  </Card>

                  <View style={styles.itemsCard}>
                    <View style={styles.itemsList}>
                      <GroceryDraggableList
                        uncheckedItems={uncheckedItems}
                        checkedItems={checkedItems}
                        onReorder={handleReorder}
                        renderItem={renderDraggableItem}
                        dragOverlayBounds={dragOverlayBounds}
                      />
                    </View>
                    <AddItemInput
                      onAddItem={handleAddItem}
                      placeholder="Add item..."
                      disabled={isAddingItem}
                    />
                  </View>
                </>
              )}
            </View>
          )}
          </ScrollView>
        )}
      </View>

      {/* New List Dialog */}
      <Modal
        visible={showNewListDialog}
        transparent
        animationType="fade"
        onRequestClose={() => setShowNewListDialog(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Create New List</Text>
            <Input
              value={newListName}
              onChangeText={setNewListName}
              placeholder={newListPlaceholder}
              onSubmitEditing={handleCreateList}
            />
            {createListError ? (
              <Text style={styles.modalErrorText}>{createListError.message}</Text>
            ) : null}
            <View style={styles.modalActions}>
              <Button variant="outline" onPress={() => setShowNewListDialog(false)} disabled={createListLoading} style={styles.modalButton}>
                Cancel
              </Button>
              <Button onPress={handleCreateList} loading={createListLoading} disabled={createListLoading} style={styles.modalButton}>
                Create
              </Button>
            </View>
          </View>
        </View>
      </Modal>

      {/* Delete List Confirmation Modal */}
      <Modal
        visible={showDeleteConfirmModal}
        transparent
        animationType="fade"
        onRequestClose={() => !deleteListLoading && setShowDeleteConfirmModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Delete list</Text>
            <Text style={styles.modalDescription}>
              {`Are you sure you want to delete "${listToDelete?.name ?? ''}"? This cannot be undone.`}
            </Text>
            <View style={styles.modalActions}>
              <Button
                variant="outline"
                onPress={() => setShowDeleteConfirmModal(false)}
                disabled={deleteListLoading}
                style={styles.modalButton}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onPress={handleConfirmDeleteList}
                loading={deleteListLoading}
                disabled={deleteListLoading}
                style={styles.modalButton}
              >
                Delete
              </Button>
            </View>
          </View>
        </View>
      </Modal>

      {/* Reset List Confirmation Modal */}
      <Modal
        visible={showResetConfirmModal}
        transparent
        animationType="fade"
        onRequestClose={() => !resetListLoading && setShowResetConfirmModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Reset list</Text>
            <Text style={styles.modalDescription}>
              Remove all items from this list? You will start with an empty list. This cannot be undone.
            </Text>
            <View style={styles.modalActions}>
              <Button
                variant="outline"
                onPress={() => setShowResetConfirmModal(false)}
                disabled={resetListLoading}
                style={styles.modalButton}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onPress={handleConfirmResetList}
                loading={resetListLoading}
                disabled={resetListLoading}
                style={styles.modalButton}
              >
                Reset
              </Button>
            </View>
          </View>
        </View>
      </Modal>

      {/* Remove item from list confirmation modal */}
      <Modal
        visible={showRemoveItemModal}
        transparent
        animationType="fade"
        onRequestClose={() => !removeItemLoading && setShowRemoveItemModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Remove item</Text>
            <Text style={styles.modalDescription}>
              {`Remove "${itemToRemove?.name ?? ''}" from the list?`}
            </Text>
            <View style={styles.modalActions}>
              <Button
                variant="outline"
                onPress={() => setShowRemoveItemModal(false)}
                disabled={removeItemLoading}
                style={styles.modalButton}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onPress={handleConfirmRemoveItem}
                loading={removeItemLoading}
                disabled={removeItemLoading}
                style={styles.modalButton}
              >
                Remove
              </Button>
            </View>
          </View>
        </View>
      </Modal>

      {/* Alert modal (limit reached, errors) */}
      <Modal
        visible={showAlertModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAlertModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{alertModalTitle}</Text>
            <Text style={styles.modalDescription}>{alertModalMessage}</Text>
            <View style={styles.modalActions}>
              <Button onPress={() => setShowAlertModal(false)} style={styles.modalButton}>
                OK
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 0,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 16,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  scrollView: {
    flex: 1,
    minWidth: 0,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 100,
    alignSelf: 'center',
    width: '100%',
    flexGrow: 1,
  },
  pageTitle: {
    fontSize: Platform.select({ ios: 24, android: 24, default: 28 }),
    fontWeight: 'bold',
    color: Brand.primary,
    marginBottom: Platform.select({ ios: 8, android: 8, default: 12 }),
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Platform.select({ ios: 8, android: 8, default: 12 }),
    gap: Platform.select({ ios: 6, android: 6, default: 10 }),
  },
  tabsScroll: {
    flex: 1,
    minWidth: 0,
  },
  headerIconButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerIconButtonDisabled: {
    opacity: 0.5,
  },
  newListIconButton: {
    backgroundColor: Brand.primary,
  },
  deleteIconButton: {
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: Neutrals.destructiveBorder,
  },
  tabsContent: {
    paddingVertical: Platform.select({ ios: 2, android: 2, default: 4 }),
    paddingRight: Platform.select({ ios: 6, android: 6, default: 8 }),
    flexDirection: 'row',
    alignItems: 'center',
  },
  tabPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Platform.select({ ios: 10, android: 10, default: 14 }),
    paddingVertical: Platform.select({ ios: 6, android: 6, default: 8 }),
    borderRadius: 999,
    backgroundColor: Neutrals.whiteFaded80,
    borderWidth: 2,
    borderColor: 'transparent',
    marginRight: Platform.select({ ios: 6, android: 6, default: 8 }),
  },
  tabPillActive: {
    backgroundColor: Brand.primary,
    borderColor: Brand.primary,
  },
  tabPillText: {
    fontSize: 13,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    maxWidth: 120,
  },
  tabPillTextActive: {
    color: '#fff',
  },
  tabPillBadge: {
    marginLeft: 6,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabPillBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: Neutrals.textPrimary,
  },
  tabPillBadgeTextActive: {
    color: Neutrals.whiteFaded90,
  },
  newListButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Brand.primary,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  newListButtonDisabled: {
    opacity: 0.6,
    backgroundColor: Neutrals.surface,
  },
  newListButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 16,
  },
  newListButtonTextDisabled: {
    color: Neutrals.textMuted,
  },
  limitMessage: {
    alignSelf: 'center',
    fontSize: 12,
    color: Neutrals.textMuted,
    marginRight: 8,
  },
  listContent: {
    gap: Platform.select({ ios: 10, android: 10, default: 16 }),
  },
  itemsCard: {
    backgroundColor: Neutrals.whiteFaded85,
    borderRadius: 20,
    overflow: 'hidden',
    padding: 16,
  },
  itemsList: {
    gap: 0,
    marginBottom: 8,
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  addItemButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: Brand.primary,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  addItemButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 16,
  },
  resetButton: {
    flexDirection: 'row',
    gap: 6,
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: Neutrals.border,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  resetButtonText: {
    color: Neutrals.textSecondary,
    fontSize: 14,
    lineHeight: 16,
  },
  resetButtonTextDisabled: {
    color: Neutrals.textMuted,
  },
  deleteButton: {
    flexDirection: 'row',
    gap: 6,
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: Neutrals.destructiveBorder,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  deleteButtonText: {
    color: Brand.destructive,
    fontSize: 14,
    lineHeight: 16,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonDisabledText: {
    color: Neutrals.destructiveSoft,
  },
  progressCard: {
    marginBottom: Platform.select({ ios: 10, android: 10, default: 16 }),
    backgroundColor: Neutrals.whiteFaded85,
    borderRadius: Platform.select({ ios: 16, android: 16, default: 20 }),
    overflow: 'hidden',
  },
  progressContent: {
    padding: Platform.select({ ios: 12, android: 12, default: 16 }),
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: Platform.select({ ios: 8, android: 8, default: 12 }),
  },
  progressLabel: {
    fontSize: Platform.select({ ios: 15, android: 15, default: 16 }),
    fontWeight: '600',
    color: Neutrals.textPrimary,
  },
  progressValue: {
    fontSize: Platform.select({ ios: 13, android: 13, default: 14 }),
    fontWeight: 'bold',
    color: Brand.primary,
  },
  progressBar: {
    height: Platform.select({ ios: 10, android: 10, default: 12 }),
    backgroundColor: Neutrals.surface,
    borderRadius: 999,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: Brand.primary,
    borderRadius: 999,
  },
  dragHandleSlot: {
    width: 10,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
  /** Wrapper so the ~38px handle can extend outside the 10px slot (negative margins) and stay visible on iOS */
  dragHandleOverflow: {
    marginLeft: -14,
    marginRight: -14,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
    paddingHorizontal: 0,
    borderBottomWidth: 1,
    borderBottomColor: RowNeutrals.border,
    overflow: 'visible',
  },
  itemRowChecked: {
    backgroundColor: 'rgba(17, 24, 39, 0.03)',
    borderRadius: 10,
  },
  itemRowLastInSection: {
    borderBottomWidth: 0,
  },
  itemRowCardBg: {
    backgroundColor: Neutrals.whiteFaded85,
    marginHorizontal: -16,
    paddingHorizontal: 16,
  },
  listCardTop: {
    backgroundColor: Neutrals.whiteFaded85,
    borderTopLeftRadius: Platform.select({ ios: 16, android: 16, default: 20 }),
    borderTopRightRadius: Platform.select({ ios: 16, android: 16, default: 20 }),
    paddingTop: Platform.select({ ios: 12, android: 12, default: 16 }),
    marginHorizontal: -16,
  },
  listCardBottom: {
    backgroundColor: Neutrals.whiteFaded85,
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    padding: 16,
    marginHorizontal: -16,
  },
  checkboxTouchTarget: {
    minWidth: 44,
    minHeight: 44,
    marginLeft: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  itemNameBlock: {
    flex: 1,
    marginLeft: 12,
    minWidth: 0,
    minHeight: 44,
    justifyContent: 'center',
  },
  itemText: {
    fontSize: 16,
    fontWeight: '700',
    color: RowNeutrals.text,
  },
  itemNameInput: {
    padding: 0,
    margin: 0,
    borderWidth: 0,
    borderBottomWidth: 1,
    borderBottomColor: Neutrals.border,
    backgroundColor: 'transparent',
    minHeight: 24,
  },
  itemNamePressable: {
    alignSelf: 'stretch',
  },
  itemTextStrikethrough: {
    textDecorationLine: 'line-through',
    textDecorationColor: '#6b7280',
    color: '#6b7280',
  },
  quantityArea: {
    minWidth: 80,
    marginLeft: 12,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  quantityValueTouchTarget: {
    minWidth: 44,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'flex-end',
    paddingLeft: 12,
  },
  quantityValueOnly: {
    fontSize: 14,
    fontWeight: '600',
  },
  quantityExpandedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  quantityBtn: {
    minWidth: 44,
    minHeight: 44,
    padding: 10,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
  },
  quantityBtnPressed: {
    backgroundColor: primaryAlpha(0.15),
  },
  quantityLabel: {
    fontSize: 14,
    fontWeight: '600',
    minWidth: 44,
    textAlign: 'center',
  },
  rowClearButton: {
    minWidth: 44,
    minHeight: 44,
    marginLeft: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  rowClearButtonPressed: {
    opacity: 0.7,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 10,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 12,
    textAlign: 'center',
  },
  modalErrorText: {
    fontSize: 14,
    color: Neutrals.destructiveMuted,
    marginTop: 8,
    textAlign: 'center',
  },
  modalDescription: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  modalButton: {
    flex: 1,
  },
});
